import { useState, useCallback } from 'react';
import { ChatMessage, ChatSession } from '../models';
import chatApi from '../../services/chatApi';
import { getSession } from '../../utils/utils';
import * as Sentry from '@sentry/react';

/**
 * Custom hook for managing chat state
 * Handles messages, sessions, loading states, and API interactions
 */
export const useChatState = ({ onSessionUpdate = null } = {}) => {
    const [messages, setMessages] = useState([]);
    const [currentSession, setCurrentSession] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [error, setError] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);

    /**
     * Initialize chat session and load history
     */
    const initializeChat = useCallback(async () => {
        if (isInitialized) return;

        try {
            setIsLoading(true);
            setError(null);

            // Create new session for this chat
            const userSession = getSession();
            const newSession = ChatSession.createNew(userSession?.id);
            setCurrentSession(newSession);

            // Add welcome message
            const welcomeMessage = ChatMessage.createBotMessage(
                "Hello! I'm Shika AI, your intelligent assistant. I can help you with information about your account, tokens, subscriptions, and answer questions about our platform. How can I assist you today?",
                newSession.sessionId
            );

            setMessages([welcomeMessage]);
            setIsInitialized(true);

        } catch (error) {
            console.error('Failed to initialize chat:', error);
            setError('Failed to initialize chat. Please refresh the page and try again.');
            Sentry.captureException(error, { tags: { context: 'chat-initialization' } });
        } finally {
            setIsLoading(false);
        }
    }, [isInitialized]);

    /**
     * Send a new message
     */
    const sendMessage = useCallback(async (messageText) => {
        if (!messageText?.trim() || isLoading || !currentSession) {
            return;
        }

        const userMessage = ChatMessage.createUserMessage(
            messageText.trim(),
            currentSession.sessionId
        );

        try {
            // Add user message immediately
            setMessages(prev => [...prev, userMessage]);
            setIsLoading(true);
            setIsTyping(true);
            setError(null);

            // Send to API with retry
            const response = await chatApi.withRetry(
                () => chatApi.sendMessage(messageText.trim(), currentSession.sessionId),
                3, // max retries
                1000 // base delay
            );

            console.log('API Response:', {
                sent_session_id: currentSession.sessionId,
                received_session_id: response.session_id,
                message_id: response.message_id
            });

            // Create bot response message
            const botMessage = ChatMessage.fromApiResponse({
                message_id: response.message_id,
                response: response.response,
                message_type: 'bot',
                timestamp: response.timestamp,
                session_id: response.session_id,
                metadata: response.metadata || {}
            });

            // Update messages and session
            setMessages(prev => [...prev, botMessage]);
            
            // Update session with new session_id from server response and message count
            setCurrentSession(prev => {
                console.log('Updating session state:', {
                    current_session_id: prev?.sessionId,
                    new_session_id: response.session_id,
                    has_updateSessionId: !!(prev && prev.updateSessionId),
                    session_type: typeof prev
                });
                
                if (prev && prev.updateSessionId && prev.incrementMessageCount) {
                    // Update session ID if it changed (server created new session)
                    if (prev.sessionId !== response.session_id) {
                        console.log(`🔄 Session ID updated: ${prev.sessionId} -> ${response.session_id}`);
                        prev.updateSessionId(response.session_id);
                    } else {
                        console.log('✅ Session ID unchanged:', response.session_id);
                    }
                    prev.incrementMessageCount(2); // user + bot message
                    return prev;
                } else {
                    console.log('⚠️ Using fallback session update');
                    // Fallback for plain objects
                    return {
                        ...prev,
                        sessionId: response.session_id, // Update session ID
                        messageCount: (prev?.messageCount || 0) + 2,
                        lastActivityAt: new Date()
                    };
                }
            });

            // Notify parent component about session update for refreshing sessions list
            if (onSessionUpdate) {
                onSessionUpdate(response.session_id, userMessage.message);
            }

        } catch (error) {
            console.error('Failed to send message:', error);
            
            // Create error message
            const errorMessage = ChatMessage.createBotMessage(
                `I apologize, but I encountered an error: ${error.message}. Please try again.`,
                currentSession.sessionId
            );
            errorMessage.metadata = { isError: true };

            setMessages(prev => [...prev, errorMessage]);
            setError(error.message);
            
            Sentry.captureException(error, { 
                tags: { context: 'chat-send-message' },
                extra: { messageText, sessionId: currentSession.sessionId }
            });

        } finally {
            setIsLoading(false);
            setIsTyping(false);
        }
    }, [currentSession, isLoading]);

    /**
     * Load chat history for current or specified session
     */
    const loadChatHistory = useCallback(async (sessionId = null, limit = 50, offset = 0) => {
        try {
            setIsLoading(true);
            setError(null);

            const response = await chatApi.getChatHistory(sessionId, limit, offset);
            
            // Convert API response to ChatMessage objects
            const historyMessages = response.messages.map(msg => 
                ChatMessage.fromApiResponse(msg)
            );

            if (offset === 0) {
                // Replace messages if loading from beginning
                setMessages(historyMessages);
            } else {
                // Append if loading more (pagination)
                setMessages(prev => [...historyMessages, ...prev]);
            }

            return {
                messages: historyMessages,
                totalCount: response.total_count,
                hasMore: historyMessages.length === limit
            };

        } catch (error) {
            console.error('Failed to load chat history:', error);
            setError(`Failed to load chat history: ${error.message}`);
            
            Sentry.captureException(error, { 
                tags: { context: 'chat-load-history' },
                extra: { sessionId, limit, offset }
            });

            return { messages: [], totalCount: 0, hasMore: false };
        } finally {
            setIsLoading(false);
        }
    }, []);

    /**
     * Switch to a different session and load its history
     */
    const switchToSession = useCallback(async (sessionId) => {
        if (!sessionId) {
            console.error('Session ID is required for switching sessions');
            return;
        }

        try {
            setIsLoading(true);
            setError(null);

            // Create a session object for the selected session
            const session = ChatSession.fromSessionId(sessionId);
            setCurrentSession(session);

            // Load chat history for this session
            const historyResult = await loadChatHistory(sessionId);
            
            if (historyResult.messages.length === 0) {
                // If no history found, add a welcome message for this session
                const welcomeMessage = ChatMessage.createBotMessage(
                    "Welcome back! I'm Shika AI, ready to continue our conversation. How can I help you today?",
                    sessionId
                );
                setMessages([welcomeMessage]);
            }

            setIsInitialized(true);
            
            console.log(`Switched to session ${sessionId} with ${historyResult.messages.length} messages`);

        } catch (error) {
            console.error('Failed to switch session:', error);
            setError(`Failed to switch to session: ${error.message}`);
            
            Sentry.captureException(error, { 
                tags: { context: 'chat-switch-session' },
                extra: { sessionId }
            });
        } finally {
            setIsLoading(false);
        }
    }, [loadChatHistory]);

    /**
     * Start a new chat session
     */
    const startNewSession = useCallback(() => {
        const userSession = getSession();
        const newSession = ChatSession.createNew(userSession?.id);
        
        setCurrentSession(newSession);
        setMessages([]);
        setError(null);
        setIsInitialized(false);
        
        // Re-initialize with new session
        initializeChat();
    }, [initializeChat]);

    /**
     * Clear all messages in current session
     */
    const clearMessages = useCallback(() => {
        setMessages([]);
        setError(null);
    }, []);

    /**
     * Get user context (for debugging or user info display)
     */
    const getUserContext = useCallback(async () => {
        try {
            const context = await chatApi.getUserContext();
            return context;
        } catch (error) {
            console.error('Failed to get user context:', error);
            Sentry.captureException(error, { tags: { context: 'chat-user-context' } });
            return null;
        }
    }, []);

    /**
     * Retry last failed message
     */
    const retryLastMessage = useCallback(() => {
        if (messages.length === 0) return;

        // Find the last user message
        const lastUserMessage = [...messages].reverse().find(msg => msg.isUserMessage());
        
        if (lastUserMessage) {
            // Remove messages after the last user message (failed bot responses)
            const lastUserIndex = messages.findLastIndex(msg => msg.id === lastUserMessage.id);
            setMessages(prev => prev.slice(0, lastUserIndex + 1));
            
            // Resend the message
            sendMessage(lastUserMessage.message);
        }
    }, [messages, sendMessage]);

    return {
        // State
        messages,
        currentSession,
        isLoading,
        isTyping,
        error,
        isInitialized,

        // Actions
        initializeChat,
        sendMessage,
        loadChatHistory,
        switchToSession,
        startNewSession,
        clearMessages,
        getUserContext,
        retryLastMessage,

        // Computed values
        messageCount: messages.length,
        hasMessages: messages.length > 0,
        canSendMessage: !isLoading && currentSession && isInitialized,
    };
}; 